import { Away, CHAT_CHANNELS, MUTE_ALL, fullNameKey, isBadge, isChatChannel, muteCovers, type ChatChannel, type MuteScope } from "odyssey-shared";
import { sameLayout, type HudLayout } from "../hud/slots.ts";
import { readChatInput } from "./command.ts";
import type { CardFacts } from "./hover-card.ts";
import type { ChatEvent, ChatFeed, ChatRequest, ChatSend } from "./events.ts";
import type { BadgeAsk } from "../badge/command.ts";
import type { ModerationAsk } from "../moderation/command.ts";
import type { PartyRequest } from "../party/members.ts";
import { NameLookups, type LookupTimers, type NameMatch } from "./lookup.ts";
import { FamilyBadges } from "./family-badges.ts";
import { chatName, sharedFirstNames } from "./names.ts";
import { PING_COUNT, PingRun, type PingProbe, type PingProgress } from "./ping.ts";
import { pingPanel, type PingPanel } from "./ping-panel.ts";
import { RecentWhisperers } from "./recent.ts";
import type { StepContext } from "./stepper.ts";
import { commandNamed, type ChatCommand, type PartyStanding } from "./commands.ts";
import { blockListLine, closedSentence, muteTime, pageRefusalSentence, pingingLine, refusalSentence, scopeWords } from "./words.ts";
import { say } from "../../words.ts";

/**
 * The chat panel's store (ADR-068 decision 8 and its amendments): what the panel shows, owned by the page rather
 * than by React, on the HUD store's pattern. The scene hands it the chat frames its connection reads and the way
 * to send on that connection; the panel hands it what the player types and the lookup its stepper wants. It tells
 * its listeners only when something it shows changes, and its snapshot is replaced on each change and kept
 * otherwise, as `useSyncExternalStore` needs. It keeps the recent whisperers a reply reaches and the page's name
 * lookups, and gives the stepper what it reads beside the draft. A line that refuses or fails is an error line,
 * which the panel sets apart; plain information is a system line (R-0027); a death is a death line, which the
 * panel draws in red behind a skull (ADR-068's amendment of 2026-10-03). It runs `/ping` (ADR-068's amendment
 * "`/ping`"), one run at a time, whose one line it changes in place as the run goes and ends as a panel of numbers
 * (its amendment "`/ping` as a network panel"). It holds the character's channels and its account's mutes as the
 * gateway last sent them (its amendment "Global, Trade and Help"), says in a system line what changed in them,
 * refuses on the page a line to a channel left or muted, and keeps Global, Trade and Help closed until the
 * gateway has sent them, as a gateway without them never does.
 */

/**
 * The most lines the log keeps; older lines leave it.
 */
export const MAX_CHAT_LINES = 100;

/**
 * What a line with no badge spreads into its fields: one frozen empty object, so the common line allocates none.
 */
const NO_BADGE_FIELD: { readonly badge?: number } = Object.freeze({});

/**
 * How far a mute's end may move between two `channels` frames, from the time each took to arrive, and still be the
 * same mute rather than a new one the page says.
 */
const MUTE_DRIFT_MS = 2000;

/**
 * One line of the log: a Local line or an action with its speaker's two names, a line of Global, Trade or Help with
 * its channel and its speaker's names, a whisper each way with the other party's names, one of the page's lines of
 * information, one saying a character died, one saying what refused or failed, or `/ping`'s panel,
 * whose numbers the log words and lays out. `id` is the line's key, unique while the page runs. A Local line, an action,
 * a channel line and a whisper carry the staff badge their speaker wore when the line arrived (ADR-112 decision 5.4),
 * absent for none; the line keeps it, whatever the speaker wears later.
 */
export type ChatLine =
  | { readonly id: number; readonly kind: "local"; readonly firstName: string; readonly familyName: string; readonly text: string; readonly badge?: number }
  | { readonly id: number; readonly kind: "channel"; readonly channel: ChatChannel; readonly firstName: string; readonly familyName: string; readonly text: string; readonly badge?: number }
  | { readonly id: number; readonly kind: "party"; readonly firstName: string; readonly familyName: string; readonly text: string }
  | { readonly id: number; readonly kind: "action"; readonly firstName: string; readonly familyName: string; readonly text: string; readonly badge?: number }
  | { readonly id: number; readonly kind: "whisper"; readonly sent: boolean; readonly firstName: string; readonly familyName: string; readonly text: string; readonly badge?: number }
  | { readonly id: number; readonly kind: "system"; readonly text: string }
  | { readonly id: number; readonly kind: "death"; readonly text: string }
  | { readonly id: number; readonly kind: "error"; readonly text: string }
  | { readonly id: number; readonly kind: "ping"; readonly panel: PingPanel };

/**
 * One mute the account carries: what it covers and when it ends, on the page's clock.
 */
export interface HeldMute {
  readonly scope: MuteScope;
  readonly until: number;
}

/**
 * The character's channels and its account's mutes, as the gateway last sent them.
 */
export interface ChannelState {
  readonly joined: readonly ChatChannel[];
  readonly muted: readonly HeldMute[];
}

/**
 * What the panel shows: whether a gateway has spoken chat on this page (the panel's showing), whether the
 * current connection does (the input's sending), the log, the block list, the channels and mutes (null until a
 * gateway that has them sends them), whether the character is in a party, until when the account's messages wait (0 for no wait), the names the latest
 * lookup answered and until when the account's lookups wait, and the HUD's logical area.
 */
export interface ChatSnapshot {
  readonly shown: boolean;
  readonly connected: boolean;
  readonly lines: readonly ChatLine[];
  readonly blocks: readonly string[];
  readonly channels: ChannelState | null;
  readonly party: boolean;
  readonly throttledUntil: number;
  readonly lookedUp: readonly NameMatch[];
  readonly invitable: readonly NameMatch[];
  readonly lookupWaitUntil: number;
  readonly layout: HudLayout;
  /** The latest whisper the player menu asked the input to start, numbered so the same name can be asked again. */
  readonly whisperAsked?: { readonly seq: number; readonly firstName: string; readonly familyName: string };
}

/**
 * What a wanted lookup is for: a name to whisper to, or a name `/invite` may offer.
 */
export type LookupPurpose = "name" | "invite";

/**
 * What the panel reads: the snapshot, a way to hear that it changed, what to do with the player's input, the
 * lookup the stepper wants, and what the stepper reads beside the draft. Each works unbound, as React calls them.
 */
export interface ChatSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => ChatSnapshot;
  readonly submit: (draft: string) => { readonly clear: boolean };
  readonly want: (prefix: string | null, purpose?: LookupPurpose) => void;
  readonly stepContext: () => StepContext;
  /** Opens the player menu on a name in the log at a point in client CSS pixels (the event's `clientX` and `clientY`, not the canvas's device pixels); absent where no menu opens. */
  readonly menuOn?: (name: NameMatch, x: number, y: number) => void;
  /** Reads the facts the hover card lists for the character a full name names (ADR-068's amendment of 2026-10-27); null for one the page does not show. */
  readonly cardOf?: (full: string) => CardFacts | null;
}

/**
 * The store's clock; where a Local line or an action goes beside the log, the bubbles over the speaker; the
 * page's own names, the players in view and the player's own; the timers of the lookups and of `/ping`; and the
 * monotonic clock `/ping` times its round trips on, so a change of the wall clock mid-run skews no sample (the
 * store's clock when a test gives one, else `performance.now`). `moderation` takes a GM's moderation command
 * (ADR-056's amendment of 2026-10-04) and answers whether it did: a page the gateway offered nothing answers false,
 * and the command is then no command, as for a player.
 */
export interface ChatStoreOptions {
  readonly now?: () => number;
  readonly monotonic?: () => number;
  readonly onLocal?: (line: { readonly handle: number; readonly firstName: string; readonly familyName: string; readonly text: string }) => void;
  readonly inView?: () => readonly NameMatch[];
  /** Reads the facts the hover card lists for the character a full name names; null for one the page does not draw. */
  readonly cardOf?: (full: string) => CardFacts | null;
  readonly self?: () => NameMatch | null;
  /** The player's standing in a party, read from the page's party store. */
  readonly standing?: () => PartyStanding;
  readonly timers?: LookupTimers;
  readonly moderation?: (ask: ModerationAsk) => boolean;
  /**
   * Takes a `/badge` command (ADR-112 decision 5.5) and answers whether it was dealt with: a page the gateway sent no
   * `staffBadgeOffer` answers false, and the command is then no command, as for a player.
   */
  readonly badge?: (ask: BadgeAsk) => boolean;
  /**
   * Takes a party request typed as a command (`/invite`, `/leave party`, `/remove`, `/promote`, `/grant`) and answers
   * whether it was sent; the party store, which words its refusals. Absent, the request goes out as any chat request.
   */
  readonly party?: (request: PartyRequest) => boolean;
  /** Opens the player menu on a character at a point in client CSS pixels (not device pixels). */
  readonly menu?: (name: NameMatch, x: number, y: number) => void;
  /**
   * The staff badge the page holds for an entity, 0 to 15, 0 for none and for a handle the page no longer holds: the
   * badge a Local line or an action carries, read when the line arrives (ADR-112 decision 5.4).
   */
  readonly badgeOf?: (handle: number) => number;
}

/**
 * A family's name as the list holds it, ignoring the case it was typed in.
 *
 * @param families The list.
 * @param typed The name as typed.
 * @returns The name as listed, or the name as typed when the list lacks it.
 */
function listedAs(families: readonly string[], typed: string): string {
  const lower = typed.toLowerCase();
  return families.find((family) => family.toLowerCase() === lower) ?? typed;
}

/**
 * The store.
 *
 * @example
 * ```ts
 * const chat = new ChatStore(hudLayout(view), { onLocal: (line) => gameReport.bubbles.say(line), inView: () => gameReport.players, self: () => gameReport.name });
 * chat.bind((request) => connection.sendChat(request));
 * chat.receive({ kind: "blocks", families: [] });
 * chat.submit("well met");
 * ```
 */
export class ChatStore implements ChatSource, ChatFeed {
  private snapshot: ChatSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly now: () => number;
  private readonly monotonic: () => number;
  private send: ChatSend = () => false;
  private nextId = 1;
  private asked: { readonly kind: "block" | "unblock"; readonly family: string } | null = null;
  private askedChannel: { readonly channel: ChatChannel; readonly joined: boolean } | null = null;
  private channelsFresh = true;
  private afkOpen = false;
  private ownAway: number = Away.Active;
  private readonly recent = new RecentWhisperers();
  private readonly families = new FamilyBadges();
  private readonly lookups: NameLookups;
  private readonly inviteLookups: NameLookups;
  private shownStanding: PartyStanding = "none";
  private run: PingRun | null = null;
  private pingLine: number | null = null;
  private probe: PingProbe | null = null;

  /**
   * @param layout The HUD's logical area.
   * @param options The clock, the bubbles' hook, the page's own names and the lookups' timers.
   */
  public constructor(
    layout: HudLayout,
    private readonly options: ChatStoreOptions = {},
  ) {
    this.now = options.now ?? Date.now;
    this.monotonic = options.monotonic ?? options.now ?? (() => performance.now());
    this.snapshot = { shown: false, connected: false, lines: [], blocks: [], channels: null, party: false, throttledUntil: 0, lookedUp: [], invitable: [], lookupWaitUntil: 0, layout };
    this.lookups = new NameLookups({ send: (prefix) => this.snapshot.connected && this.send({ kind: "lookup", prefix }), now: this.now, ...(options.timers === undefined ? {} : { timers: options.timers }) });
    this.inviteLookups = new NameLookups({ send: (prefix) => this.snapshot.connected && this.send({ kind: "inviteLookup", prefix }), now: this.now, ...(options.timers === undefined ? {} : { timers: options.timers }) });
  }

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * What the panel shows now.
   *
   * @returns The snapshot, the same object until something changes.
   */
  public readonly getSnapshot = (): ChatSnapshot => this.snapshot;

  /**
   * Sets the HUD's logical area, as the window's size or scale changes, or the viewer's Ability bar choice (`barFive`).
   *
   * @param layout The area.
   */
  public setLayout(layout: HudLayout): void {
    const now = this.snapshot.layout;
    if (!sameLayout(now, layout)) {
      this.change({ layout });
    }
  }

  /**
   * Gives the store the way to send on the scene's connection.
   *
   * @param send The sender.
   */
  public bind(send: ChatSend): void {
    this.send = send;
  }

  /**
   * Gives the store the probe `/ping` times the network with beside each ping (ADR-068's amendment "`/ping`
   * measures the network"); a run started before it has none, and without one a run reports the socket's measure.
   *
   * @param probe The probe, or null for none.
   */
  public bindProbe(probe: PingProbe | null): void {
    this.probe = probe;
  }

  /**
   * Says which prefix the stepper wants looked up as the draft changes; the lookup is sent once it stands.
   *
   * @param prefix The prefix, or null when the draft wants none.
   * @param purpose What the lookup is for: a name to whisper to (the default) or one `/invite` may offer, which is asked
   *   of the gateway's invite lookup and whose answer is forgotten as soon as it is no longer wanted.
   */
  public readonly want = (prefix: string | null, purpose: LookupPurpose = "name"): void => {
    this.lookups.want(purpose === "name" ? prefix : null);
    if (purpose === "invite" && prefix !== null) {
      this.inviteLookups.want(prefix);
      return;
    }
    this.inviteLookups.reset();
    if (this.snapshot.invitable.length > 0) {
      this.change({ invitable: [] });
    }
  };

  /**
   * The facts the hover card lists for the character a full name names, read from the page's view when the card opens.
   *
   * @param full The character's full name.
   * @returns The facts, or null for a character the page does not draw.
   */
  public readonly cardOf = (full: string): CardFacts | null => this.options.cardOf?.(full) ?? null;

  /**
   * Opens the player menu on a name in the log.
   *
   * @param name The character.
   * @param x The point's x in client CSS pixels.
   * @param y The point's y in client CSS pixels.
   */
  public readonly menuOn = (name: NameMatch, x: number, y: number): void => {
    this.options.menu?.(name, x, y);
  };

  /**
   * Asks the panel to start a whisper to a character: its input holds `/w First Family ` and takes the focus. The
   * player menu's Whisper calls it.
   *
   * @param name The character.
   */
  public readonly startWhisper = (name: NameMatch): void => {
    this.change({ whisperAsked: { seq: (this.snapshot.whisperAsked?.seq ?? 0) + 1, firstName: name.firstName, familyName: name.familyName } });
  };

  /**
   * What the stepper reads beside the draft: the players in view but the player's own, the player's own name, the
   * latest lookup's names, the recent whisperers newest first and the block list.
   *
   * @returns The context.
   */
  public readonly stepContext = (): StepContext => {
    const self = this.options.self?.() ?? null;
    const own = self === null ? null : fullNameKey(self.firstName, self.familyName);
    const inView = (this.options.inView?.() ?? []).filter((name) => name.firstName !== "" && fullNameKey(name.firstName, name.familyName) !== own).map((name) => ({ firstName: name.firstName, familyName: name.familyName }));
    return { inView, self, lookedUp: this.snapshot.lookedUp, invitable: this.snapshot.invitable, whisperers: this.recent.list(this.now(), this.snapshot.blocks), blocks: this.snapshot.blocks, party: this.currentStanding() };
  };

  /**
   * The player's standing in a party as the page's party store holds it; none while the page wires no store.
   *
   * @returns The standing, which is also remembered so a change can be told from a repeat.
   */
  private currentStanding(): PartyStanding {
    this.shownStanding = this.options.standing?.() ?? "none";
    return this.shownStanding;
  }

  /**
   * Tells the panel the player's party standing may have changed (the party store's roster changed), so a list
   * already open follows it at once. Listeners hear only when the standing differs from the one last read.
   */
  public readonly standingChanged = (): void => {
    if ((this.options.standing?.() ?? "none") !== this.shownStanding) {
      this.change({});
    }
  };

  /**
   * The badge a line from an entity carries, as the field the line holds: the badge the page holds for the handle when
   * the line arrives, nothing for none.
   *
   * @param handle The speaker's entity handle.
   * @returns The line's `badge` field, or nothing.
   */
  private badgeOfHandle(handle: number): { readonly badge?: number } {
    const badge = this.options.badgeOf?.(handle) ?? 0;
    return badge > 0 && isBadge(badge) ? { badge } : NO_BADGE_FIELD;
  }

  /**
   * The badge a line from a family carries, as the field the line holds: the one `speakerBadge` told, nothing for none.
   *
   * @param familyName The speaker's family.
   * @returns The line's `badge` field, or nothing.
   */
  private badgeOfFamily(familyName: string): { readonly badge?: number } {
    const badge = this.families.of(familyName);
    return badge > 0 ? { badge } : NO_BADGE_FIELD;
  }

  /**
   * Takes a chat frame the connection read, or its end.
   *
   * @param event The event.
   */
  public receive(event: ChatEvent): void {
    switch (event.kind) {
      case "said":
        this.options.onLocal?.(event);
        this.add({ kind: "local", firstName: event.firstName, familyName: event.familyName, text: event.text, ...this.badgeOfHandle(event.handle) });
        return;
      case "posed":
        this.options.onLocal?.({ handle: event.handle, firstName: event.firstName, familyName: event.familyName, text: `*${event.text}*` });
        this.add({ kind: "action", firstName: event.firstName, familyName: event.familyName, text: event.text, ...this.badgeOfHandle(event.handle) });
        return;
      case "whispered":
        if (!event.sent) {
          this.recent.heard(event.firstName, event.familyName, this.now());
        }
        this.add({ kind: "whisper", sent: event.sent, firstName: event.firstName, familyName: event.familyName, text: event.text, ...this.badgeOfFamily(event.familyName) });
        return;
      case "blocks":
        this.listed(event.families);
        return;
      case "refused":
        if (event.reason === "rate") {
          this.change({ throttledUntil: this.now() + event.waitMs });
        } else {
          this.add({ kind: "error", text: refusalSentence(event.reason, event.waitMs) });
        }
        return;
      case "matches":
        this.lookups.receive(event.prefix, event.names, event.waitMs);
        if (event.waitMs > 0) {
          this.inviteLookups.holdFor(event.waitMs);
        }
        this.change({ lookedUp: this.lookups.matches(""), lookupWaitUntil: this.lookups.waitUntil });
        return;
      case "inviteMatches":
        this.inviteLookups.receive(event.prefix, event.names, event.waitMs);
        if (event.waitMs > 0) {
          this.lookups.holdFor(event.waitMs);
        }
        this.change({ invitable: this.inviteLookups.matches(""), lookupWaitUntil: this.inviteLookups.waitUntil });
        return;
      case "pong":
        this.run?.receive(event.t);
        return;
      case "channelSaid":
        if (isChatChannel(event.channel)) {
          this.add({ kind: "channel", channel: event.channel, firstName: event.firstName, familyName: event.familyName, text: event.text, ...this.badgeOfFamily(event.familyName) });
        }
        return;
      case "partySaid":
        this.add({ kind: "party", firstName: event.firstName, familyName: event.familyName, text: event.text });
        return;
      case "party":
        if (this.snapshot.party !== event.members.length > 0) {
          this.change({ party: event.members.length > 0 });
        }
        return;
      case "channels":
        this.channelsHeld(event.joined, event.muted);
        return;
      case "away":
        this.awayHeld(event.state);
        return;
      case "speakerBadge":
        this.families.tell(event.familyName, event.badge);
        return;
      case "whisperAway":
        this.add({ kind: "system", text: say("chat.afkWhisper", { name: chatName(event, sharedFirstNames(this.snapshot.lines, event)) }) });
        return;
      case "channelMuted":
        this.add({ kind: "system", text: event.minutes === 0 ? say("chat.familyUnmuted", { family: event.familyName, scope: scopeWords(event.channel) }) : say("chat.familyMuted", { family: event.familyName, scope: scopeWords(event.channel), time: muteTime(event.minutes * 60_000) }) });
        return;
      case "ended":
        this.families.clear();
        this.run?.end();
        this.run = null;
        this.pingLine = null;
        this.lookups.ended();
        this.inviteLookups.ended();
        this.channelsFresh = true;
        this.askedChannel = null;
        this.afkOpen = false;
        this.ownAway = Away.Active;
        if (this.snapshot.party) {
          this.change({ party: false });
        }
        if (this.snapshot.connected) {
          this.change({ connected: false });
        }
        return;
    }
  }

  /**
   * Takes the player's own away state (ADR-090 decision 6): the first one opens `/afk`, and becoming AFK and leaving
   * AFK each write a system line; idle writes nothing.
   *
   * @param state The state the server sent.
   */
  private awayHeld(state: number): void {
    this.afkOpen = true;
    if (state === Away.Afk && this.ownAway !== Away.Afk) {
      this.add({ kind: "system", text: say("chat.nowAfk") });
    } else if (state !== Away.Afk && this.ownAway === Away.Afk) {
      this.add({ kind: "system", text: say("chat.noLongerAfk") });
    }
    this.ownAway = state;
  }

  /**
   * Adds a line of the page's own to the log as a system line; nothing is sent.
   *
   * @param text The sentence.
   */
  public note(text: string): void {
    this.add({ kind: "system", text });
  }

  /**
   * Adds a death line to the log (ADR-068's amendment of 2026-10-03): a line saying a character died, such as the
   * page's own death line, which is a death line because its caller says so, never because of its words; nothing is
   * sent.
   *
   * @param text The sentence, such as "Spider killed you with Bite.".
   */
  public death(text: string): void {
    this.add({ kind: "death", text });
  }

  /**
   * Forgets the character the page played, as Change character must before the next joins (P1.9-T62): the
   * connection's end as `ended` takes it, then the log, the recent whisperers a reply reaches, the lookups' answer
   * and waits, the block list, the channels and mutes, and the message wait, so nothing of one character, or of one account, reaches the
   * next. The panel stays shown and its layout kept; the next connection sends its own block list.
   */
  public forget(): void {
    this.receive({ kind: "ended" });
    this.recent.clear();
    this.lookups.forget();
    this.inviteLookups.forget();
    this.asked = null;
    this.change({ lines: [], blocks: [], channels: null, party: false, throttledUntil: 0, lookedUp: [], invitable: [], lookupWaitUntil: 0 });
  }

  /**
   * Acts on what the player typed and sent from the input: sends a request, lists the block list, or says why
   * nothing was sent.
   *
   * @param draft The input's text.
   * @returns Whether the input should be cleared: yes once it is dealt with, no when it was refused or held, so
   *   the player can change it.
   */
  public readonly submit = (draft: string): { readonly clear: boolean } => {
    const intent = readChatInput(draft);
    switch (intent.kind) {
      case "leave":
        return { clear: true };
      case "refused":
        return this.refuse(pageRefusalSentence(intent.reason));
      case "closed":
        return this.refuse(closedSentence(intent.command));
      case "moderation":
        if (this.options.moderation?.(intent.ask) !== true) {
          return this.refuse(pageRefusalSentence("command"));
        }
        return { clear: intent.ask.kind !== "usage" };
      case "badge":
        if (this.options.badge?.(intent.ask) !== true) {
          return this.refuse(pageRefusalSentence("command"));
        }
        return { clear: intent.ask.kind !== "usage" };
      case "blocks":
        this.add({ kind: "system", text: this.snapshot.blocks.length === 0 ? say("chat.noBlocks") : blockListLine(this.snapshot.blocks) });
        return { clear: true };
      case "clear":
        this.change({ lines: [] });
        return { clear: true };
      case "reply": {
        const to = this.recent.list(this.now(), this.snapshot.blocks)[0];
        return to === undefined ? this.refuse(pageRefusalSentence("nobody")) : this.message({ kind: "whisper", firstName: to.firstName, familyName: to.familyName, text: intent.text });
      }
      case "say":
      case "pose":
      case "whisper":
        return this.message(intent);
      case "block":
      case "unblock": {
        const sent = this.request({ kind: intent.kind, familyName: intent.familyName });
        if (sent.clear) {
          this.asked = { kind: intent.kind, family: intent.familyName };
        }
        return sent;
      }
      case "ping":
        return this.ping();
      case "afk": {
        const command = commandNamed("afk");
        if (!this.afkOpen && command !== undefined) {
          return this.refuse(closedSentence(command));
        }
        return this.request({ kind: "afk" });
      }
      case "channel":
        return this.channelLine(intent.channel, intent.text, intent.command);
      case "party":
        return this.partyLine(intent.text);
      case "partyRequest":
        return { clear: (this.options.party ?? ((request) => this.request(request).clear))(intent.request) };
      case "membership": {
        if (this.snapshot.channels === null) {
          return this.refuse(closedSentence(intent.command));
        }
        const sent = this.request(intent.joined ? { kind: "channelJoin", channel: intent.channel } : { kind: "channelLeave", channel: intent.channel });
        if (sent.clear) {
          this.askedChannel = { channel: intent.channel, joined: intent.joined };
        }
        return sent;
      }
      case "mute": {
        const command = commandNamed(intent.minutes === 0 ? "unmute" : "mute");
        if (this.snapshot.channels === null && command !== undefined) {
          return this.refuse(closedSentence(command));
        }
        return this.request({ kind: "channelMute", familyName: intent.familyName, channel: intent.channel, minutes: intent.minutes });
      }
    }
  };

  /**
   * Sends a line to a channel, or refuses it on the page: closed while the gateway has sent no channels, and
   * refused when the character left the channel or a mute covers it, each in an error line keeping the draft.
   *
   * @param channel The channel.
   * @param text The line, as the text rule admitted it.
   * @param command The command that named it, for its closed answer.
   * @returns Whether the input should be cleared.
   */
  private channelLine(channel: ChatChannel, text: string, command: ChatCommand): { readonly clear: boolean } {
    const held = this.snapshot.channels;
    if (held === null) {
      return this.refuse(closedSentence(command));
    }
    if (!held.joined.includes(channel)) {
      return this.refuse(say("chat.notJoined", { channel: say(`chat.scopes.${channel}`), word: channel }));
    }
    const now = this.now();
    const wait = held.muted.reduce((longest, mute) => (muteCovers(mute.scope, channel) && mute.until - now > longest ? mute.until - now : longest), 0);
    if (wait > 0) {
      return this.refuse(say("chat.muted", { scope: say(`chat.scopes.${channel}`), time: muteTime(wait) }));
    }
    return this.message({ kind: "channelSay", channel, text });
  }

  /**
   * Sends a line to Party (ADR-101 decision 25), or refuses it on the page, in an error line keeping the draft: while
   * the character is in no party, and while a mute of all stands, which is the one mute that covers Party.
   *
   * @param text The line, as the text rule admitted it.
   * @returns Whether the input should be cleared.
   */
  private partyLine(text: string): { readonly clear: boolean } {
    if (!this.snapshot.party) {
      return this.refuse(say("chat.refused.alone"));
    }
    const now = this.now();
    const wait = (this.snapshot.channels?.muted ?? []).reduce((longest, mute) => (mute.scope === MUTE_ALL && mute.until - now > longest ? mute.until - now : longest), 0);
    if (wait > 0) {
      return this.refuse(say("chat.muted", { scope: say("chat.scopes.party"), time: muteTime(wait) }));
    }
    return this.message({ kind: "partySay", text });
  }

  /**
   * Takes the character's channels and its account's mutes: the first after a welcome says only the mutes it
   * carries; a later one says which channels it joined or left, or that a join or a leave asked for was already so,
   * and which mutes started or were lifted. A channel or a scope this build does not know is left out.
   *
   * @param joined The channels, as sent.
   * @param muted The mutes, as sent, each with its time left.
   */
  private channelsHeld(joined: readonly string[], muted: readonly { readonly channel: string; readonly waitMs: number }[]): void {
    const now = this.now();
    const next: ChannelState = {
      joined: CHAT_CHANNELS.filter((channel) => joined.includes(channel)),
      muted: muted.flatMap((mute) => (mute.channel === MUTE_ALL || isChatChannel(mute.channel) ? [{ scope: mute.channel as MuteScope, until: now + mute.waitMs }] : [])),
    };
    const before = this.channelsFresh ? null : this.snapshot.channels;
    const lines: string[] = [];
    if (before !== null) {
      for (const channel of CHAT_CHANNELS) {
        const was = before.joined.includes(channel);
        const is = next.joined.includes(channel);
        if (is !== was) {
          lines.push(is ? say("chat.joined", { channel: say(`chat.scopes.${channel}`) }) : say("chat.left", { channel: say(`chat.scopes.${channel}`) }));
        }
      }
      const asked = this.askedChannel;
      if (lines.length === 0 && asked !== null && next.joined.includes(asked.channel) === asked.joined) {
        lines.push(asked.joined ? say("chat.alreadyJoined", { channel: say(`chat.scopes.${asked.channel}`) }) : say("chat.alreadyLeft", { channel: say(`chat.scopes.${asked.channel}`) }));
      }
    }
    const held = (before?.muted ?? []).filter((mute) => mute.until > now);
    for (const mute of next.muted) {
      const earlier = held.find((old) => old.scope === mute.scope);
      if (earlier === undefined || Math.abs(earlier.until - mute.until) > MUTE_DRIFT_MS) {
        lines.push(say("chat.muted", { scope: scopeWords(mute.scope), time: muteTime(mute.until - now) }));
      }
    }
    for (const mute of held) {
      if (!next.muted.some((still) => still.scope === mute.scope)) {
        lines.push(say("chat.unmuted", { scope: scopeWords(mute.scope) }));
      }
    }
    this.channelsFresh = false;
    this.askedChannel = null;
    this.change({ channels: next, lines: this.appended(lines.map((text) => ({ kind: "system", text }))) });
  }

  /**
   * Starts a `/ping` run, unless one is going or there is no connection in chat to run it on. The account's message
   * wait does not hold it back, since a ping is no message.
   *
   * @returns Whether the input should be cleared.
   */
  private ping(): { readonly clear: boolean } {
    if (this.run?.running === true) {
      return this.refuse(say("chat.pingRunning"));
    }
    if (!this.snapshot.connected) {
      return this.refuse(say("chat.notConnected"));
    }
    const run = new PingRun({
      send: (t) => this.snapshot.connected && this.send({ kind: "ping", t }),
      ...(this.probe === null ? {} : { probe: this.probe }),
      now: this.monotonic,
      ...(this.options.timers === undefined ? {} : { timers: this.options.timers }),
      show: (progress) => this.pinged(progress),
    });
    this.run = run;
    this.pingLine = null;
    if (!run.start()) {
      this.run = null;
      return this.refuse(say("chat.notConnected"));
    }
    return { clear: true };
  }

  /**
   * Shows a run's progress in its one line: the countdown as a system line, then the panel in its place, or an
   * error line when no reply came back.
   *
   * @param progress What the run told.
   */
  private pinged(progress: PingProgress): void {
    if (progress.kind === "counting") {
      this.pingLine = this.put(this.pingLine, { kind: "system", text: pingingLine(progress.sent) });
      return;
    }
    const panel = pingPanel({ socket: progress.samples, network: progress.network }, PING_COUNT);
    this.put(this.pingLine, panel === null ? { kind: "error", text: say("chat.noReply") } : { kind: "ping", panel });
    this.run = null;
    this.pingLine = null;
  }

  /**
   * Changes a line of the log in place, keeping its id and its place, or adds it at the end when there is no such
   * line or it has left the log.
   *
   * @param id The line's id, or null for a new line.
   * @param line The line, without its id.
   * @returns The id the line has now.
   */
  private put(id: number | null, line: DistributiveOmit<ChatLine, "id">): number {
    const at = id === null ? -1 : this.snapshot.lines.findIndex((held) => held.id === id);
    if (id === null || at === -1) {
      const added = this.nextId;
      this.add(line);
      return added;
    }
    const lines = [...this.snapshot.lines];
    lines[at] = { ...line, id } as ChatLine;
    this.change({ lines });
    return id;
  }

  /**
   * Sends a message unless the account's messages wait, when the draft is kept.
   *
   * @param request The message.
   * @returns Whether the input should be cleared.
   */
  private message(request: ChatRequest): { readonly clear: boolean } {
    if (this.now() < this.snapshot.throttledUntil) {
      return { clear: false };
    }
    return this.request(request);
  }

  /**
   * Adds an error line of the page's own to the log, such as a GM's action the gateway refused (ADR-056's amendment
   * of 2026-10-04); nothing is sent.
   *
   * @param text The sentence.
   */
  public fail(text: string): void {
    this.add({ kind: "error", text });
  }

  /**
   * Says in an error line why nothing was sent, keeping the draft.
   *
   * @param sentence The sentence.
   * @returns That the input keeps its draft.
   */
  private refuse(sentence: string): { readonly clear: boolean } {
    this.add({ kind: "error", text: sentence });
    return { clear: false };
  }

  /**
   * Sends a request, or says in an error line that there is no connection to send it on.
   *
   * @param request The request.
   * @returns Whether the input should be cleared.
   */
  private request(request: ChatRequest): { readonly clear: boolean } {
    if (!this.snapshot.connected || !this.send(request)) {
      return this.refuse(say("chat.notConnected"));
    }
    return { clear: true };
  }

  /**
   * Takes the account's block list: the first after a welcome shows the panel and says nothing; a later one says
   * which family joined or left it, or that a family a block asked for was already on it.
   *
   * @param families The list.
   */
  private listed(families: readonly string[]): void {
    const before = this.snapshot.blocks;
    const reconnected = !this.snapshot.connected;
    const lines: string[] = [];
    if (!reconnected) {
      const had = new Set(before.map((family) => family.toLowerCase()));
      const has = new Set(families.map((family) => family.toLowerCase()));
      for (const family of families) {
        if (!had.has(family.toLowerCase())) {
          lines.push(say("chat.blocked", { family }));
        }
      }
      for (const family of before) {
        if (!has.has(family.toLowerCase())) {
          lines.push(say("chat.unblocked", { family }));
        }
      }
      const asked = this.asked;
      if (lines.length === 0 && asked !== null && asked.kind === "block" && has.has(asked.family.toLowerCase())) {
        lines.push(say("chat.alreadyBlocked", { family: listedAs(families, asked.family) }));
      }
    }
    this.asked = null;
    const same = !reconnected && lines.length === 0 && before.length === families.length && before.every((family, i) => family === families[i]);
    if (!same) {
      this.change({ shown: true, connected: true, blocks: [...families], lines: this.appended(lines.map((text) => ({ kind: "system", text }))) });
    }
  }

  /**
   * Adds a line to the log.
   *
   * @param line The line, without its id.
   */
  private add(line: DistributiveOmit<ChatLine, "id">): void {
    this.change({ lines: this.appended([line]) });
  }

  /**
   * The log with lines added at its end, their ids given, and its oldest lines past `MAX_CHAT_LINES` dropped.
   *
   * @param added The lines, without their ids.
   * @returns The log.
   */
  private appended(added: readonly DistributiveOmit<ChatLine, "id">[]): readonly ChatLine[] {
    if (added.length === 0) {
      return this.snapshot.lines;
    }
    const lines = [...this.snapshot.lines, ...added.map((line) => ({ ...line, id: this.nextId++ }) as ChatLine)];
    return lines.length > MAX_CHAT_LINES ? lines.slice(lines.length - MAX_CHAT_LINES) : lines;
  }

  /**
   * Replaces the snapshot with some of its fields changed and tells the listeners.
   *
   * @param fields The fields that changed.
   */
  private change(fields: Partial<ChatSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...fields };
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * `Omit` over each member of a union, so a line's kind still decides its fields.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
